import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../utils/axios';
import mapPointService from '../../services/mapPointService';
import PointForm from './components/PointForm';
import PointMap from './components/PointMap';
import PointsTable from './components/PointsTable';
import AddressImportPanel from './components/AddressImportPanel';
import {
  buildDistancePairs,
  buildPointFlags,
  parseCoordinatesInput,
  validatePointPayload
} from './utils/geo';
import { checkNearbyInfrastructure } from './services/overpassService';

const fetchReferenceData = async (endpoint) => {
  try {
    const response = await axiosInstance.get(endpoint);
    return Array.isArray(response?.data?.data) ? response.data.data : [];
  } catch {
    return [];
  }
};

const emptyImportFilters = {
  cityId: '',
  districtId: '',
  productId: '',
  packagingId: '',
  courierId: '',
  dateFrom: '',
  dateTo: ''
};

const emptyForm = {
  name: '',
  coordinates: ''
};

const normalizePoint = (item, source = 'loaded') => {
  const latitude = Number(item.latitude);
  const longitude = Number(item.longitude);
  const sourceAddressDoc = item?.sourceAddressId && typeof item.sourceAddressId === 'object'
    ? item.sourceAddressId
    : null;
  const sourceAddressId = sourceAddressDoc?._id || (typeof item?.sourceAddressId === 'string' ? item.sourceAddressId : null);

  const createdAtRaw =
    item.createdAt ||
    item.created_at ||
    item.updatedAt ||
    item.updated_at ||
    Date.now();

  return {
    pointKey: String(item._id || item.id || `${source}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    sourceId: item._id || item.id || null,
    name: String(item.name || '').trim(),
    latitude,
    longitude,
    sourceAddress: {
      id: sourceAddressId ? String(sourceAddressId) : null,
      status: sourceAddressDoc?.status || null,
      photo: sourceAddressDoc?.photo || null
    },
    sortTimestamp: Number(new Date(createdAtRaw)) || Date.now(),
    infrastructure: {
      status: 'idle',
      distanceMeters: null,
      amenity: null,
      title: null,
      errorMessage: null
    }
  };
};

const MapPointsWorkspace = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [points, setPoints] = useState([]);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [creatingPoint, setCreatingPoint] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [formValues, setFormValues] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [products, setProducts] = useState([]);
  const [packagings, setPackagings] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [importFilters, setImportFilters] = useState(emptyImportFilters);
  const [importCandidates, setImportCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [importingCandidates, setImportingCandidates] = useState(false);
  const [importSummary, setImportSummary] = useState({ total: 0, importedAlreadyCount: 0 });
  const [bulkInactivatingAddresses, setBulkInactivatingAddresses] = useState(false);
  const [updatingAddressPointKey, setUpdatingAddressPointKey] = useState(null);
  const [showDistanceGrid, setShowDistanceGrid] = useState(true);

  const pairs = useMemo(() => buildDistancePairs(points), [points]);
  const pointFlags = useMemo(
    () => buildPointFlags({ points, pairs }),
    [points, pairs]
  );
  const blueAddressPoints = useMemo(
    () => points.filter((point) => {
      if (!point.sourceAddress?.id) return false;
      const flags = pointFlags[point.pointKey] || {};
      return !flags.proximityViolation;
    }),
    [pointFlags, points]
  );

  const updatePointInfrastructure = useCallback((pointKey, nextInfrastructure) => {
    setPoints((prev) =>
      prev.map((point) =>
        point.pointKey === pointKey
          ? {
              ...point,
              infrastructure: {
                ...point.infrastructure,
                ...nextInfrastructure
              }
            }
          : point
      )
    );
  }, []);

  const runInfrastructureCheck = useCallback(
    async (pointKey, latitude, longitude) => {
      updatePointInfrastructure(pointKey, {
        status: 'checking',
        errorMessage: null,
        distanceMeters: null,
        amenity: null,
        title: null
      });

      try {
        const result = await checkNearbyInfrastructure({ latitude, longitude, radius: 50 });

        if (result.status === 'found') {
          updatePointInfrastructure(pointKey, {
            status: 'found',
            distanceMeters: result.distanceMeters,
            amenity: result.amenity,
            title: result.title,
            errorMessage: null
          });
        } else {
          updatePointInfrastructure(pointKey, {
            status: 'not_found',
            distanceMeters: null,
            amenity: null,
            title: null,
            errorMessage: null
          });
        }
      } catch (error) {
        updatePointInfrastructure(pointKey, {
          status: 'error',
          errorMessage: error?.message || 'Не удалось проверить инфраструктуру',
          distanceMeters: null,
          amenity: null,
          title: null
        });
      }
    },
    [updatePointInfrastructure]
  );

  const loadPoints = useCallback(async () => {
    try {
      setLoadingPoints(true);
      const response = await mapPointService.getMyPoints();
      const list = Array.isArray(response?.data) ? response.data : [];
      const normalized = list.map((item) => normalizePoint(item));
      setPoints(normalized);

      normalized.forEach((point) => {
        runInfrastructureCheck(point.pointKey, point.latitude, point.longitude);
      });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Ошибка загрузки точек', {
        variant: 'error'
      });
    } finally {
      setLoadingPoints(false);
    }
  }, [enqueueSnackbar, runInfrastructureCheck]);

  useEffect(() => {
    loadPoints();
  }, [loadPoints]);

  useEffect(() => {
    const loadRefs = async () => {
      const [citiesRes, districtsRes, productsRes, packagingsRes, staffRes] = await Promise.all([
        fetchReferenceData('/addresses/cities'),
        fetchReferenceData('/districts'),
        fetchReferenceData('/products'),
        fetchReferenceData('/packagings'),
        fetchReferenceData('/staff')
      ]);

      const onlyCouriers = staffRes.filter((staff) => staff?.role === 'courier');

      setCities(citiesRes);
      setDistricts(districtsRes);
      setProducts(productsRes);
      setPackagings(packagingsRes);
      setCouriers(onlyCouriers);
    };
    loadRefs();
  }, []);

  const onFormChange = useCallback(
    (field) => (event) => {
      const value = event.target.value;
      setFormValues((prev) => ({ ...prev, [field]: value }));

      if (formErrors[field]) {
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [formErrors]
  );

  const onCreatePoint = useCallback(
    async (event) => {
      event.preventDefault();

      const parsedCoordinates = parseCoordinatesInput(formValues.coordinates);
      if (!parsedCoordinates.isValid) {
        setFormErrors((prev) => ({
          ...prev,
          coordinates: parsedCoordinates.error
        }));
        enqueueSnackbar('Проверьте формат координат', { variant: 'warning' });
        return;
      }

      const validation = validatePointPayload({
        name: formValues.name,
        latitude: parsedCoordinates.latitude,
        longitude: parsedCoordinates.longitude
      });
      if (!validation.isValid) {
        setFormErrors({
          name: validation.errors.name,
          coordinates: validation.errors.latitude || validation.errors.longitude
        });
        enqueueSnackbar('Проверьте корректность введённых координат', { variant: 'warning' });
        return;
      }

      const payload = {
        name: String(formValues.name).trim(),
        latitude: parsedCoordinates.latitude,
        longitude: parsedCoordinates.longitude
      };

      try {
        setCreatingPoint(true);

        const response = await mapPointService.createPoint(payload);
        const created = normalizePoint(response?.data || payload, 'created');

        setPoints((prev) => [created, ...prev]);
        setFormValues(emptyForm);
        setFormErrors({});
        enqueueSnackbar('Точка добавлена', { variant: 'success' });

        runInfrastructureCheck(created.pointKey, created.latitude, created.longitude);
      } catch (error) {
        enqueueSnackbar(error.response?.data?.error || 'Ошибка создания точки', {
          variant: 'error'
        });
      } finally {
        setCreatingPoint(false);
      }
    },
    [enqueueSnackbar, formValues, runInfrastructureCheck]
  );

  const onDeletePoint = useCallback(
    async (pointKey) => {
      const target = points.find((point) => point.pointKey === pointKey);
      if (!target) return;

      try {
        if (target.sourceId) {
          await mapPointService.deletePoint(target.sourceId);
        }

        setPoints((prev) => prev.filter((point) => point.pointKey !== pointKey));
        enqueueSnackbar('Точка удалена', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(error.response?.data?.error || 'Ошибка удаления точки', {
          variant: 'error'
        });
      }
    },
    [enqueueSnackbar, points]
  );

  const onDeleteAll = useCallback(async () => {
    if (!points.length || deletingAll) return;

    const confirmed = window.confirm('Удалить все точки? Это действие нельзя отменить.');
    if (!confirmed) return;

    try {
      setDeletingAll(true);
      await mapPointService.deleteAllPoints();
      setPoints([]);
      enqueueSnackbar('Все точки удалены', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Ошибка удаления всех точек', {
        variant: 'error'
      });
    } finally {
      setDeletingAll(false);
    }
  }, [deletingAll, enqueueSnackbar, points.length]);

  const onFilterChange = useCallback((field, value) => {
    setImportFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const onApplyImportFilters = useCallback(async () => {
    try {
      setLoadingCandidates(true);
      const response = await mapPointService.getImportAddressCandidates(importFilters);
      setImportCandidates(Array.isArray(response?.data) ? response.data : []);
      setImportSummary({
        total: Number(response?.total || 0),
        importedAlreadyCount: Number(response?.importedAlreadyCount || 0)
      });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Ошибка загрузки адресов', { variant: 'error' });
    } finally {
      setLoadingCandidates(false);
    }
  }, [enqueueSnackbar, importFilters]);

  const onImportAddresses = useCallback(async () => {
    if (importingCandidates) return;

    try {
      setImportingCandidates(true);
      const response = await mapPointService.importFromAddresses(importFilters);
      const importedCount = Number(response?.importedCount || 0);
      const skippedExistingCount = Number(response?.skippedExistingCount || 0);
      const skippedInvalidCount = Number(response?.skippedInvalidCount || 0);

      await loadPoints();
      await onApplyImportFilters();

      enqueueSnackbar(
        `Импорт адресов завершён. Добавлено: ${importedCount}, уже были: ${skippedExistingCount}, некорректные: ${skippedInvalidCount}`,
        { variant: importedCount > 0 ? 'success' : 'info' }
      );
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Ошибка импорта адресов', {
        variant: 'error'
      });
    } finally {
      setImportingCandidates(false);
    }
  }, [enqueueSnackbar, importFilters, importingCandidates, loadPoints, onApplyImportFilters]);

  const onCopyNames = useCallback(async () => {
    if (!points.length) {
      enqueueSnackbar('Нет точек для копирования', { variant: 'info' });
      return;
    }

    const text = points
      .map((point) => String(point.name || '').trim())
      .filter(Boolean)
      .join('\n');

    if (!text) {
      enqueueSnackbar('Названия точек пустые', { variant: 'warning' });
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar('Названия скопированы', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Не удалось скопировать в буфер обмена', { variant: 'error' });
    }
  }, [enqueueSnackbar, points]);

  const onCopyAddressPhoto = useCallback(async (point) => {
    const value = String(point?.sourceAddress?.photo || '').trim();
    if (!value) {
      enqueueSnackbar('У выбранного адреса пустое поле photo', { variant: 'warning' });
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar('Адрес скопирован', { variant: 'success' });
    } catch {
      enqueueSnackbar('Не удалось скопировать адрес', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const onSetAddressActive = useCallback(async (point) => {
    const addressId = point?.sourceAddress?.id;
    if (!addressId) return;

    try {
      setUpdatingAddressPointKey(point.pointKey);
      await axiosInstance.patch(`/addresses/${addressId}/status`, { status: 'active' });

      setPoints((prev) => prev.map((item) => (
        item.pointKey === point.pointKey
          ? {
              ...item,
              sourceAddress: {
                ...item.sourceAddress,
                status: 'active'
              }
            }
          : item
      )));

      enqueueSnackbar('Адрес возвращен в active', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Не удалось вернуть адрес в active', { variant: 'error' });
    } finally {
      setUpdatingAddressPointKey(null);
    }
  }, [enqueueSnackbar]);

  const onCopyAllAddressPhotos = useCallback(async () => {
    const text = blueAddressPoints
      .map((point) => String(point?.sourceAddress?.photo || '').trim())
      .filter(Boolean)
      .join('\n');

    if (!text) {
      enqueueSnackbar('Нет адресов для копирования', { variant: 'warning' });
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar('Все адреса скопированы', { variant: 'success' });
    } catch {
      enqueueSnackbar('Не удалось скопировать адреса', { variant: 'error' });
    }
  }, [blueAddressPoints, enqueueSnackbar]);

  const onSetAddressInactive = useCallback(async (point) => {
    const addressId = point?.sourceAddress?.id;
    if (!addressId) return;

    try {
      setUpdatingAddressPointKey(point.pointKey);
      await axiosInstance.patch(`/addresses/${addressId}/status`, { status: 'inactive' });

      setPoints((prev) => prev.map((item) => (
        item.pointKey === point.pointKey
          ? {
              ...item,
              sourceAddress: {
                ...item.sourceAddress,
                status: 'inactive'
              }
            }
          : item
      )));

      enqueueSnackbar('Адрес переведен в inactive', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Не удалось перевести адрес в inactive', { variant: 'error' });
    } finally {
      setUpdatingAddressPointKey(null);
    }
  }, [enqueueSnackbar]);

  const onSetAllAddressesInactive = useCallback(async () => {
    const targets = blueAddressPoints.filter(
      (point) => point.sourceAddress?.id && point.sourceAddress?.status !== 'inactive'
    );

    if (!targets.length) {
      enqueueSnackbar('Нет адресов для перевода в inactive', { variant: 'info' });
      return;
    }

    try {
      setBulkInactivatingAddresses(true);

      const results = await Promise.allSettled(
        targets.map((point) => axiosInstance.patch(`/addresses/${point.sourceAddress.id}/status`, { status: 'inactive' }))
      );

      const successAddressIds = new Set(
        results
          .map((result, index) => (result.status === 'fulfilled' ? targets[index].sourceAddress.id : null))
          .filter(Boolean)
      );

      if (successAddressIds.size) {
        setPoints((prev) => prev.map((item) => (
          successAddressIds.has(item.sourceAddress?.id)
            ? {
                ...item,
                sourceAddress: {
                  ...item.sourceAddress,
                  status: 'inactive'
                }
              }
            : item
        )));
      }

      const failedCount = targets.length - successAddressIds.size;
      enqueueSnackbar(
        failedCount > 0
          ? `В inactive переведено: ${successAddressIds.size}, ошибок: ${failedCount}`
          : `Все адреса переведены в inactive (${successAddressIds.size})`,
        { variant: failedCount > 0 ? 'warning' : 'success' }
      );
    } catch {
      enqueueSnackbar('Не удалось выполнить массовый перевод адресов', { variant: 'error' });
    } finally {
      setBulkInactivatingAddresses(false);
    }
  }, [blueAddressPoints, enqueueSnackbar]);

  return (
    <Box p={2}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
        Гео-инструмент точек
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Красный маркер: есть соседняя точка ближе 30м. Красное свечение: рядом инфраструктура (&lt;50м).
      </Alert>

      <Box
        sx={{
          mb: 2,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(320px, 5fr) minmax(420px, 7fr)'
          }
        }}
      >
        <Box>
          <Card>
            <CardContent>
              <PointForm
                values={formValues}
                errors={formErrors}
                onChange={onFormChange}
                onSubmit={onCreatePoint}
                isSubmitting={creatingPoint}
              />
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                sx={{ mb: 1 }}
              >
                <Tab label="Точки" />
                <Tab label="Импорт адресов" />
              </Tabs>

              {activeTab === 0 && (
                <PointsTable
                  points={points}
                  pointFlags={pointFlags}
                  onDeletePoint={onDeletePoint}
                  onDeleteAll={onDeleteAll}
                  onCopyNames={onCopyNames}
                  deletingAll={deletingAll}
                />
              )}

              {activeTab === 1 && (
                <AddressImportPanel
                  filters={importFilters}
                  onFilterChange={onFilterChange}
                  cities={cities}
                  districts={districts}
                  products={products}
                  packagings={packagings}
                  couriers={couriers}
                  candidates={importCandidates}
                  loading={loadingCandidates}
                  importing={importingCandidates}
                  summary={importSummary}
                  onApplyFilters={onApplyImportFilters}
                  onImport={onImportAddresses}
                />
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h6">Карта точек</Typography>
              <FormControlLabel
                control={(
                  <Switch
                    checked={showDistanceGrid}
                    onChange={(event) => setShowDistanceGrid(event.target.checked)}
                    size="small"
                  />
                )}
                label="Сетка расстояний"
              />
            </Box>
            <PointMap
              points={points}
              pointFlags={pointFlags}
              pairs={pairs}
              onDeletePoint={onDeletePoint}
              showDistanceGrid={showDistanceGrid}
            />
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h6">Адреса с синими точками</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={onSetAllAddressesInactive}
                  disabled={blueAddressPoints.length === 0 || bulkInactivatingAddresses}
                >
                  {bulkInactivatingAddresses ? 'Переводим...' : 'Перевести в неактивные все адреса'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={onCopyAllAddressPhotos}
                  disabled={blueAddressPoints.length === 0}
                >
                  Скопировать все адреса
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Точка</TableCell>
                    <TableCell>Статус адреса</TableCell>
                    <TableCell>photo</TableCell>
                    <TableCell align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blueAddressPoints.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        Нет синих точек с адресами
                      </TableCell>
                    </TableRow>
                  ) : (
                    blueAddressPoints.map((point) => {
                      const status = String(point.sourceAddress?.status || 'unknown');
                      const isInactive = status === 'inactive';
                      const photo = String(point.sourceAddress?.photo || '').trim();

                      return (
                        <TableRow key={`address-row-${point.pointKey}`} hover>
                          <TableCell>{point.name}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={status}
                              color={isInactive ? 'default' : 'success'}
                              variant={isInactive ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 520, wordBreak: 'break-all' }}>
                            {photo || '—'}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              {isInactive ? (
                                <Button
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  onClick={() => onSetAddressActive(point)}
                                  disabled={updatingAddressPointKey === point.pointKey || bulkInactivatingAddresses}
                                >
                                  {updatingAddressPointKey === point.pointKey ? '...' : 'Вернуть в активный'}
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  onClick={() => onSetAddressInactive(point)}
                                  disabled={updatingAddressPointKey === point.pointKey || bulkInactivatingAddresses}
                                >
                                  {updatingAddressPointKey === point.pointKey ? '...' : 'Перевести в неактивный'}
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onCopyAddressPhoto(point)}
                                disabled={!photo}
                              >
                                Скопировать адрес
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {loadingPoints && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Загружаем точки...
        </Typography>
      )}
    </Box>
  );
};

export default MapPointsWorkspace;
